// CAT Mock 6 — VARC section.
const catMock6Varc = [
  {
    id: 1,
    type: "mcq",
    passageId: "rc1",
    passage: `Much of the software on which the modern economy runs was written by people who were not paid to write it, or not paid by anyone who would own the result. Open-source projects — whose code anyone may read, copy and modify — supply the operating systems of most web servers, the encryption that protects online payments and countless small libraries that other programs quietly depend upon. To an economist of an earlier generation this would have looked impossible. Why would rational people give away valuable work?

Several answers have been offered, and each captures something. Some contributors are motivated by the pleasure of solving problems and by the esteem of peers whose judgement they respect. Others build a public record of skill that later helps them find employment. Increasingly, the largest contributors are companies, which pay staff to improve shared code because it is cheaper to maintain a common foundation than for each firm to build its own, and because they earn their money from services built on top of it.

The success, however, conceals a fragility. Commercial firms contribute to the projects that matter to their products; less glamorous components are often maintained by one or two volunteers in their spare time. When a serious security flaw was discovered in 2014 in a widely used encryption library, it emerged that the project, on which a large share of the world's secure websites depended, had been receiving only a few thousand dollars a year in donations. The episode prompted a flurry of funding, much of which later dried up. The economics of open source are not so much a refutation of self-interest as a demonstration of its limits: people and firms will invest in a commons when their own returns are visible, and neglect it where they are not.`,
    question: "The author mentions the security flaw discovered in 2014 primarily to:",
    options: [
      "A. argue that open-source software is less secure than commercial software.",
      "B. show that donations are the most reliable way of funding open-source projects.",
      "C. illustrate how a component that many depend on can nevertheless be badly under-resourced.",
      "D. criticise the volunteers who maintained the encryption library."
    ],
    answer: "C"
  },
  {
    id: 2,
    type: "mcq",
    passageId: "rc1",
    question: "Which of the following is NOT mentioned in the passage as a reason why people or firms contribute to open-source projects?",
    options: [
      "A. A legal obligation to publish code that is used commercially.",
      "B. The respect of skilled peers.",
      "C. Building a visible record of ability that helps in finding work.",
      "D. The lower cost of sharing a common foundation compared with each firm building its own."
    ],
    answer: "A"
  },
  {
    id: 3,
    type: "mcq",
    passageId: "rc1",
    question: "The last sentence of the passage suggests that the open-source phenomenon:",
    options: [
      "A. proves that people are fundamentally altruistic.",
      "B. shows that markets can supply any good without public support.",
      "C. will eventually collapse once companies stop contributing.",
      "D. is consistent with self-interest, but self-interest alone does not ensure the upkeep of components whose benefits are diffuse."
    ],
    answer: "D"
  },
  {
    id: 4,
    type: "mcq",
    passageId: "rc1",
    question: "An economist of an earlier generation would have found open source \"impossible\" chiefly because such an economist would have assumed that:",
    options: [
      "A. software could never become economically important.",
      "B. people do not produce valuable goods without expecting a direct return.",
      "C. companies would never share code with their competitors.",
      "D. volunteers lack the skills to write complex software."
    ],
    answer: "B"
  },
  {
    id: 5,
    type: "mcq",
    passageId: "rc2",
    passage: `Every autumn, a young garden warbler that has never made the journey leaves its birthplace in northern Europe and flies alone, at night, to a wintering ground in Africa it has never seen. That it can do so implies that it carries some sense of direction from birth, and decades of experiment have shown that one of its guides is the Earth's magnetic field. Caged birds in the migratory season hop restlessly in the direction in which they would fly; when the field around the cage is rotated with electric coils, the direction of their hopping rotates with it.

How birds detect the field has proved far harder to establish. One early proposal held that they carry tiny particles of magnetic iron oxide, which would act like compass needles; such particles were found in the beaks of pigeons, but many turned out to belong to immune cells with no connection to the nervous system. A stranger hypothesis has since gained ground. It holds that light striking certain proteins in the bird's eye creates pairs of molecules whose chemical behaviour is subtly sensitive to the orientation of the magnetic field — so that the bird may, in effect, see the field as a pattern overlaid on its vision. The idea explains a puzzling observation: the birds' compass works only when some light is present, and it is disrupted by weak radio-frequency noise that would be far too feeble to disturb a compass needle.

The hypothesis is not yet proven; the relevant reactions have been demonstrated in test tubes, not in living birds. But it has made the question of bird navigation unexpectedly important to physicists, since it implies that a quantum effect, usually thought too fragile to survive outside carefully controlled laboratories, may operate in the warm, wet interior of a living cell.`,
    question: "The experiment in which the magnetic field around a cage is rotated is cited as evidence that:",
    options: [
      "A. birds use the Earth's magnetic field to orient themselves.",
      "B. birds learn their migration routes from older birds.",
      "C. birds detect the magnetic field with iron particles in their beaks.",
      "D. caged birds lose their sense of direction."
    ],
    answer: "A"
  },
  {
    id: 6,
    type: "mcq",
    passageId: "rc2",
    question: "According to the passage, the iron-oxide explanation was weakened because:",
    options: [
      "A. no iron particles were ever found in birds.",
      "B. iron particles would be disturbed by light.",
      "C. many of the particles found were in cells unconnected to the nervous system.",
      "D. the particles were too small to act as compass needles."
    ],
    answer: "C"
  },
  {
    id: 7,
    type: "mcq",
    passageId: "rc2",
    question: "Which observation is explained by the light-based hypothesis but would be difficult to explain if birds relied on compass-needle-like particles?",
    options: [
      "A. Young birds can migrate without having made the journey before.",
      "B. Caged birds hop in the direction of migration.",
      "C. Birds migrate at night.",
      "D. The birds' compass is disrupted by very weak radio-frequency noise."
    ],
    answer: "D"
  },
  {
    id: 8,
    type: "mcq",
    passageId: "rc2",
    question: "According to the passage, physicists have become interested in bird navigation because:",
    options: [
      "A. it has finally been proven that birds can see magnetic fields.",
      "B. it suggests that a delicate quantum effect might function inside living tissue.",
      "C. it could lead to better compasses for aircraft.",
      "D. radio-frequency noise has been shown to harm migratory birds."
    ],
    answer: "B"
  },
  {
    id: 9,
    type: "mcq",
    passageId: "rc3",
    passage: `Few debates about museums are as heated as that over the return of objects taken during colonial rule. Advocates of return point out that many such objects were acquired through conquest, looting or sales made under duress, and that their absence deprives the communities that made them of a living connection with their own past. Defenders of the great "universal" museums reply that their collections allow visitors to see the cultures of the world side by side, that objects have often been better preserved than they would have been at home, and that once the principle of return is accepted, there is no obvious place to stop.

Each of these defences has weakened under scrutiny. The argument from preservation is awkward when the objects in question were damaged during their removal, and it has aged badly as museums have been built and expanded in the countries of origin. The argument from universality assumes that the cultures of the world are best understood from a handful of cities that were themselves the centres of empire — an assumption that visitors from elsewhere may not share. The slippery-slope argument is the most serious, but it proves less than it claims: the difficulty of drawing a line is a reason to draw it carefully, not a reason to refuse to draw it at all.

What is easily lost in the polemic is that many of the most productive arrangements have not taken the form of simple transfer. Some museums have returned ownership while keeping objects on long-term loan; others circulate collections between institutions, or train curators in both countries. Such arrangements are less satisfying to partisans on either side than a clear victory, but they treat the question less as a dispute over property than as an opportunity to rebuild a relationship that the original acquisitions damaged.`,
    question: "The author's view of the \"slippery-slope\" argument is that it is:",
    options: [
      "A. the weakest of the defences offered by universal museums.",
      "B. the most serious defence, but not a sufficient reason to refuse all returns.",
      "C. a decisive argument against the return of any object.",
      "D. irrelevant to the debate about colonial-era objects."
    ],
    answer: "B"
  },
  {
    id: 10,
    type: "mcq",
    passageId: "rc3",
    question: "According to the passage, the argument from preservation has weakened partly because:",
    options: [
      "A. universal museums have failed to look after their collections.",
      "B. visitors are no longer interested in seeing objects side by side.",
      "C. most objects were bought legally.",
      "D. museums have been built and enlarged in the countries from which the objects came."
    ],
    answer: "D"
  },
  {
    id: 11,
    type: "mcq",
    passageId: "rc3",
    question: "The author implies that the argument from universality:",
    options: [
      "A. rests on a viewpoint centred on the former capitals of empires.",
      "B. is accepted by visitors from all parts of the world.",
      "C. has been strengthened by the growth of international tourism.",
      "D. applies equally to museums in the countries of origin."
    ],
    answer: "A"
  },
  {
    id: 12,
    type: "mcq",
    passageId: "rc3",
    question: "The final paragraph of the passage mainly serves to:",
    options: [
      "A. declare the advocates of return the winners of the debate.",
      "B. argue that objects should never leave the museums that hold them.",
      "C. draw attention to arrangements that go beyond a simple choice between keeping and returning objects.",
      "D. criticise curators for failing to cooperate across countries."
    ],
    answer: "C"
  },
  {
    id: 13,
    type: "mcq",
    passageId: "rc4",
    passage: `Boredom has a poor reputation. It is associated with idleness, with the restless child and the stalled afternoon, and an entire industry of entertainment exists to banish it. Psychologists who study it, however, have begun to describe it less as a failing than as a signal. Boredom, on this view, is the mind's way of reporting that what it is doing does not engage its capacities, much as pain reports damage. A signal of this kind is useful precisely because it is unpleasant: it prompts us to change course.

The trouble, some researchers argue, is that the modern world offers too easy a response. A smartphone can relieve boredom within seconds, and so the signal is answered before it has done its work. The discomfort that might once have pushed a person to take up a new project, start a conversation or simply let the mind wander is dissolved instead in a stream of small novelties that engage attention without engaging much else. In one set of experiments, people asked to sit alone with their thoughts for fifteen minutes found the experience so unpleasant that many chose to give themselves a mild electric shock rather than sit idle.

It would be easy to turn these findings into a sermon against technology, and some writers have. But the more careful conclusion is narrower. Boredom is not valuable in itself; prolonged, inescapable boredom, of the kind experienced in some workplaces and institutions, is associated with poorer health and riskier behaviour. Its value lies in what it prompts. The question is not whether we should be bored more often, but whether we allow the brief discomfort of boredom to lead somewhere before we hurry to silence it.`,
    question: "The comparison between boredom and pain is used to suggest that boredom:",
    options: [
      "A. is a sign of physical illness.",
      "B. should be avoided wherever possible.",
      "C. is unpleasant in a way that serves a purpose, by prompting a change of course.",
      "D. is felt most strongly by children."
    ],
    answer: "C"
  },
  {
    id: 14,
    type: "mcq",
    passageId: "rc4",
    question: "The experiment involving mild electric shocks is cited to show:",
    options: [
      "A. how strongly many people dislike being left with nothing but their thoughts.",
      "B. that boredom causes physical pain.",
      "C. that smartphones make people more tolerant of discomfort.",
      "D. that people prefer pain to entertainment."
    ],
    answer: "A"
  },
  {
    id: 15,
    type: "mcq",
    passageId: "rc4",
    question: "With which of the following statements would the author most likely agree?",
    options: [
      "A. People should deliberately seek out as much boredom as possible.",
      "B. Technology is the main cause of poor mental health.",
      "C. Boredom has no useful function.",
      "D. Chronic boredom that cannot be escaped is harmful rather than beneficial."
    ],
    answer: "D"
  },
  {
    id: 16,
    type: "mcq",
    passageId: "rc4",
    question: "In the final paragraph, the author distances themself from writers who:",
    options: [
      "A. study boredom in workplaces and institutions.",
      "B. treat the research as grounds for a general condemnation of technology.",
      "C. regard boredom as a signal.",
      "D. argue that boredom is linked to risky behaviour."
    ],
    answer: "B"
  },
  {
    id: 17,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. The invention of the Fresnel lens in the 1820s changed this, concentrating the light of a single lamp into a beam visible for many miles.\n2. For centuries, lighthouses were the only reliable warning of a dangerous coast at night.\n3. Today satellite navigation has made many of these lenses redundant, and most surviving lighthouses are automated or preserved as monuments.\n4. Early towers burned wood or coal, whose light was dim and easily hidden by its own smoke.",
    answer: "2413"
  },
  {
    id: 18,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. These receipts soon began to circulate as payment in their own right.\n2. Seeing their usefulness, the state took over their issue, creating one of the world's first government paper currencies.\n3. In eleventh-century China, merchants weighed down by heavy strings of iron coins began leaving them with trusted deposit houses.\n4. In return they received paper receipts that could be exchanged for the coins.",
    answer: "3412"
  },
  {
    id: 19,
    type: "tita",
    question: "The five sentences (labelled 1, 2, 3, 4, 5) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the five numbers as your answer.\n1. The most common method today, reverse osmosis, forces seawater through membranes at high pressure, which consumes large amounts of energy.\n2. Yet the process leaves behind a concentrated brine that must be disposed of without harming marine life.\n3. Nearly all the water on Earth is in the oceans, too salty to drink or to irrigate crops.\n4. Falling prices of solar power have therefore revived interest in desalination for dry coastal regions.\n5. Removing the salt is technically straightforward; the difficulty is cost.",
    answer: "35142"
  },
  {
    id: 20,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nMost of the world's antibiotics are given not to people but to farm animals, often in small daily doses to speed growth rather than to treat disease. Each such dose gives bacteria an opportunity to evolve resistance, and resistant strains do not stay on farms: they travel in meat, in water and on the hands of farm workers. Countries that have restricted the routine use of antibiotics in livestock have, for the most part, continued to produce meat at competitive prices, suggesting that the practice is less necessary than its defenders claim.",
    options: [
      "A. Antibiotics should be banned in farming because they make meat more expensive.",
      "B. Resistant bacteria are found mainly on farms and pose little risk to people.",
      "C. Routine use of antibiotics in farm animals breeds resistant bacteria that reach people, and experience suggests the practice can be curbed without great economic cost.",
      "D. Farm animals now receive more antibiotics than people do because they are more often ill."
    ],
    answer: "C"
  },
  {
    id: 21,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nStudents who take notes on laptops tend to write more than those who write by hand, often transcribing lectures almost word for word. Yet in several studies, the students who wrote by hand did better on questions that required them to understand concepts. Because writing by hand is slow, they cannot record everything and must decide what matters, summarising in their own words. The very inefficiency of the method appears to force the processing that produces understanding.",
    options: [
      "A. Laptops should be banned from lecture halls because they distract students.",
      "B. Handwritten note-taking, being slower, forces students to select and rephrase material, which seems to deepen conceptual understanding more than near-verbatim laptop notes.",
      "C. Students who write more notes always learn more.",
      "D. Students who write by hand remember more facts than those who use laptops."
    ],
    answer: "B"
  },
  {
    id: 22,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nOf the many species carried by people to new regions, only a small fraction establish themselves, and fewer still spread widely enough to cause harm. Ecologists have tried for decades to predict which ones will, with limited success. Traits such as rapid reproduction help, but so does chance: the number of individuals released, the weather in the first years and the absence of local predators. Prevention at borders therefore remains more reliable than attempts to identify the dangerous few in advance.",
    options: [
      "A. Most introduced species become invasive and cause serious harm.",
      "B. Rapid reproduction is the single best predictor of whether a species will become invasive.",
      "C. Ecologists can now accurately predict which species will become invasive.",
      "D. Because whether an introduced species becomes harmful depends heavily on chance as well as on its traits, it is hard to predict, so prevention at borders is the more dependable strategy."
    ],
    answer: "D"
  },
  {
    id: 23,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Tides are caused mainly by the gravitational pull of the Moon on the Earth's oceans.\n2. The Sun also exerts a tidal pull, though it is less than half as strong as the Moon's.\n3. When the Sun and Moon are aligned, at new and full moon, their pulls combine to produce especially high \"spring\" tides.\n4. Many coastal towns depend on fishing as their main source of income.\n5. When the two are at right angles, their pulls partly cancel, producing the weaker \"neap\" tides.",
    answer: "4"
  },
  {
    id: 24,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Engineers have borrowed folding patterns from origami to design structures that pack small and open large.\n2. Solar panels for satellites, for example, are folded tightly for launch and unfold once in orbit.\n3. Paper-folding competitions in Japan attract thousands of entrants every year.\n4. Similar folds are being tested in medical stents that expand only once they are inside a blood vessel.\n5. In each case, the challenge is to find a pattern that can open with a single, simple motion.",
    answer: "3"
  }
];

export default catMock6Varc;
